import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { translate } from "../lib/i18n";

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

const keyFor = (userId) => `pv_app_lang:${userId || "anon"}`;

/** Wrap the SC App or Customer App root with this. `persistEndpoint` is the
 * profile-update route that owns `preferred_language` for that persona
 * ("/sc/profile" or "/customer-app/profile") — CRM/Service/FSM never use
 * this provider and stay English-only.
 *
 * Language choice is remembered PER USER on this device (keyed by user id),
 * so two different people logging in on the same phone never see each
 * other's language. The very first time a given user is seen on this
 * device, their server-saved `preferred_language` wins; after that, any
 * language they pick here locally takes over (so they can freely flip it
 * without a round trip, while it still gets persisted to the server too). */
export function LanguageProvider({ children, persistEndpoint }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(() => localStorage.getItem(keyFor(null)) || "en");
  const resolvedForUser = useRef(null);

  useEffect(() => {
    if (!user || resolvedForUser.current === user.id) return;
    resolvedForUser.current = user.id;
    const own = localStorage.getItem(keyFor(user.id));
    setLangState(own || user.preferred_language || "en");
  }, [user]);

  const setLang = useCallback((code) => {
    setLangState(code);
    localStorage.setItem(keyFor(user?.id), code);
    if (persistEndpoint) {
      api.put(persistEndpoint, { preferred_language: code }).catch(() => {
        toast.error("Couldn't save your language choice — it'll still work on this device for now.");
      });
    }
  }, [persistEndpoint, user?.id]);

  const t = useCallback((key) => translate(lang, key), [lang]);

  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export const useLanguage = () => useContext(LangCtx);
